<template>
  <div class="graph-panel">
    <div class="header-row">
      <div class="segmented">
        <button
          class="seg-btn"
          :class="{ active: viewMode === 'outgoing' }"
          @click="setMode('outgoing')"
        >
          Outgoing
        </button>
        <button
          class="seg-btn"
          :class="{ active: viewMode === 'incoming' }"
          @click="setMode('incoming')"
        >
          Incoming
        </button>
      </div>

      <div class="actions">
        <button class="btn btn-secondary" @click="openMap">
          Show Map
        </button>
        <button class="btn" @click="$emit('addRequested')">
          Add new connections
        </button>
        <HelpLink topic="graph-map" title="About connections and the graph map" />
      </div>
    </div>

    <div class="list-wrapper">
      <table class="graph-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Relation</th>
            <th>Project</th>
            <th style="width:80px;">Delete</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="conn in connections" :key="conn.edge_id">
            <td class="ellipsis">
              <router-link
                class="clickable"
                :to="nodeRoute(conn.node)"
                :title="conn.node.name"
                @click="$emit('closeRequested')"
              >
                {{ conn.node.name }}
              </router-link>
            </td>
            <td class="ellipsis">
              {{ conn.node.kind }}
            </td>
            <td class="ellipsis" :title="conn.relation_type">
              {{ conn.relation_type }}
            </td>
            <td class="ellipsis" :title="projectName(conn.node.project_id)">
              {{ projectName(conn.node.project_id) }}
            </td>
            <td>
              <button
                class="icon-btn danger"
                title="Remove connection"
                @click="deleteEdge(conn.edge_id)"
              >
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>

          <tr v-if="!loading && connections.length === 0">
            <td colspan="5" class="muted">
              {{ viewMode === 'outgoing' ? 'No outgoing connections yet.' : 'No incoming connections yet.' }}
            </td>
          </tr>

          <tr v-if="loading">
            <td colspan="5" class="muted">Loading…</td>
          </tr>

          <tr v-if="error">
            <td colspan="5" class="error">{{ error }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="showMap"
      class="map-modal-backdrop"
      @click.self="handleMapBackdropClick"
    >
      <div class="map-modal" role="dialog" aria-modal="true" aria-label="Full graph map">
        <div class="map-modal-header">
          <div>
            <div class="map-modal-title">Full Map</div>
            <div class="map-modal-subtitle">Showing all reachable nodes from the selected start node.</div>
          </div>
          <button class="btn btn-secondary" @click="closeMap">Close</button>
        </div>

        <GraphFullMap
          :nodeId="nodeId"
          :fullscreen="true"
          @node-drag-end="handleMapNodeDragEnd"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_ENDPOINTS } from "@/services/config";
import GraphFullMap from "@/components/GraphFullMap.vue";
import { mapState } from "vuex";
import { useToast } from "vue-toastification";

export default {
  name: "GraphPanel",
  components: {
    GraphFullMap,
  },
  props: {
    nodeId: { type: Number, required: true },
  },
  data() {
    return {
      viewMode: "outgoing",
      showMap: false,
      connections: [],
      projects: [],
      loading: false,
      error: "",
      mapBackdropCloseBlockedUntil: 0,
    };
  },
  computed: {
    ...mapState(["loggedInUser"]),
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  mounted() {
    this.fetchLookups();
    this.fetchConnections();
  },
  beforeUnmount() {
    document.body.style.overflow = "";
  },
  methods: {
    openMap() {
      this.showMap = true;
    },
    closeMap() {
      this.showMap = false;
    },
    handleMapNodeDragEnd(payload) {
      if (payload?.moved) {
        this.mapBackdropCloseBlockedUntil = Date.now() + 400;
      }
    },
    handleMapBackdropClick() {
      if (Date.now() < this.mapBackdropCloseBlockedUntil) {
        return;
      }
      this.closeMap();
    },
    setMode(mode) {
      if (this.viewMode !== mode) {
        this.viewMode = mode;
        this.fetchConnections();
      }
    },
    async fetchConnections() {
      this.loading = true;
      this.error = "";
      try {
        const res = await axios.get(API_ENDPOINTS.graphMap, {
          params: {
            entra_id: this.loggedInUser?.entra_id,
            start_node_id: this.nodeId,
          },
        });

        const { edges = [], connected_nodes = [] } = res.data;
        const nodeMap = {};

        connected_nodes.forEach((node) => {
          nodeMap[node.id] = {
            ...node,
            kind: node.node_type || "Unknown",
          };
        });

        const filteredEdges = edges.filter((edge) => (
          this.viewMode === "outgoing"
            ? edge.src_node_id === this.nodeId
            : edge.dst_node_id === this.nodeId
        ));

        this.connections = filteredEdges.map((edge) => {
          const otherNodeId = this.viewMode === "outgoing"
            ? edge.dst_node_id
            : edge.src_node_id;

          return {
            edge_id: edge.id,
            src_node_id: edge.src_node_id,
            dst_node_id: edge.dst_node_id,
            relation_type_id: edge.relation_type_id,
            relation_type: edge.relation_type || "Unknown",
            node: nodeMap[otherNodeId] || {
              id: otherNodeId,
              name: "Unknown",
              kind: "Unknown",
              node_type: "Unknown",
            },
          };
        });
      } catch (error) {
        console.error(error);
        this.error = "Failed to load graph connections.";
      } finally {
        this.loading = false;
      }
    },
    async fetchLookups() {
      try {
        const proj = await axios.get(API_ENDPOINTS.projects, {
          params: { entra_id: this.loggedInUser?.entra_id },
        });
        this.projects = proj.data || [];
      } catch (error) {
        console.warn("Lookup fetch failed", error);
      }
    },
    projectName(id) {
      if (!id) return "—";
      const project = this.projects.find((item) => +item.id === +id);
      return project ? project.name : "—";
    },
    async deleteEdge(edgeId) {
      try {
        await axios.delete(API_ENDPOINTS.graphEdges, {
          data: {
            entra_id: this.loggedInUser?.entra_id,
            id: edgeId,
          },
        });
        this.toast.success("Connection removed.");
        await this.fetchConnections();
      } catch (error) {
        console.error(error);
        this.toast.error("Failed to remove connection.");
      }
    },
    nodeRoute(node) {
      if (node.kind === "experiment") {
        return { name: "ExperimentDetailPage", params: { id: String(node.id) } };
      }
      if (node.kind === "record") {
        return { name: "RecordDetailPage", params: { id: String(node.id) } };
      }
      if (node.kind === "instrument") {
        return { name: "InstrumentDetailPage", params: { id: String(node.id) } };
      }
      if (node.kind === "material") {
        return { name: "MaterialDetailPage", params: { id: String(node.id) } };
      }
      return "#";
    },
  },
  watch: {
    nodeId() {
      this.fetchConnections();
    },
    showMap(value) {
      document.body.style.overflow = value ? "hidden" : "";
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.graph-panel {
  background: $secondary-color;
  border-radius: 8px;
  padding: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,.08);
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    color: $primary-color;
  }
}

.actions {
  display: flex;
  gap: 8px;
}

.segmented {
  display: inline-flex;
  background: #f0f0f3;
  border: 1px solid #e3e3e7;
  border-radius: 8px;
  overflow: hidden;
}

.seg-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s ease, color .15s ease;
}

.seg-btn:not(.active):hover {
  background: #e9e9ee;
}

.seg-btn.active {
  background: #ffffff;
  color: $primary-color;
  box-shadow: inset 0 0 0 1px #e3e3e7;
}

.btn-secondary {
  background: #ffffff;
  border: 1px solid #d1d5db;
  color: #1f2937;
}

.btn-secondary:hover {
  background: #f8fafc;
}

.list-wrapper {
  background: #fff;
  border: 1px solid #e8e8ec;
  border-radius: 8px;
}

.graph-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.graph-table th {
  background: #fafafa;
}

.graph-table th,
.graph-table td {
  border-bottom: 1px solid #eee;
  padding: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.clickable {
  color: $primary-color;
  text-decoration: underline;
  cursor: pointer;
}

.muted {
  color: #7a7a7a;
  margin: 8px 0;
  text-align: center;
}

.error {
  color: #d33;
  text-align: center;
}

.ellipsis {
  text-overflow: ellipsis;
  overflow: hidden;
}

.map-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.map-modal {
  width: min(96vw, 1700px);
  height: min(92vh, 1100px);
  border-radius: 14px;
  border: 1px solid #d8dee6;
  background: #f3f5f7;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.35);
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 10px;
  padding: 12px;
}

.map-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 4px 4px 0;
}

.map-modal-title {
  color: $primary-color;
  font-size: 1.05rem;
  font-weight: 700;
}

.map-modal-subtitle {
  color: #5d6470;
  font-size: 0.88rem;
}

@media (max-width: 768px) {
  .header-row {
    flex-direction: column;
    align-items: stretch;
  }

  .actions {
    justify-content: flex-end;
  }

  .map-modal-backdrop {
    padding: 10px;
  }

  .map-modal {
    width: 100%;
    height: 95vh;
    padding: 8px;
  }
}
</style>
